import React, { useMemo, useState, useEffect } from 'react';
import '../../App.css';
import '../itSupport/itSupport.css';
import { LuRefreshCw } from "react-icons/lu";
import { SEC_ApiCall } from '../func.js';
import { ApiError } from '../common/apiError.js';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { FiExternalLink, FiInfo } from 'react-icons/fi';
import { CgSpinner } from 'react-icons/cg';
import { AssignUserWithNote } from '../common/toolbar/AssignUserWithNote.js';
import { NoteInputComponent } from '../common/toolbar/NoteInputComponent.js';
import { AssignUser } from '../common/toolbar/AssignUser.js';
import { AddNote } from '../common/toolbar/AddNote.js';
import { MarkCompleteButton } from '../common/toolbar/MarkCompleteButton.js';
import { MarkIncompleteButton } from '../common/toolbar/MarkIncompleteButton.js';
import { UpdateStatusButton } from '../common/toolbar/UpdateStatusButton.js';
import { UpdateStatusSelect } from '../common/toolbar/UpdateStatusSelect.js';
import LoadingComp from '../common/loading.js';
import Select from 'react-select';
import { userAppData } from '../MainApp.js';
import { ExpandableLinkButton } from '../common/ExpandableLinkButton.js';
import { RequestFilesList } from '../common/RequestFilesList.js';
import Header from '../common/header.js';

/**
 * Displays the Reprographics request queue for either managers or requesters.
 */
export default function ReproRequests({ requesterMode = false }) {
  const [dataState, setDataState] = useState([]);
  const [isLoadedState, setIsLoadedState] = useState(false);
  const [apiErrorState, setApiErrorState] = useState(null);
  const [apiErrorTextState, setApiErrorTextState] = useState('');
  const [requestStateFilter, setRequestStateFilter] = useState('incomplete');

  const [resultsPerPageState, setResultsPerPageState] = useState(12);
  const [currentPageState, setCurrentPageState] = useState(1);
  const [numberOfPagesState, setNumberOfPagesState] = useState(1);
  const [pageDataState, setPageDataState] = useState(null);
  const [assignableUsersState, setAssignableUsersState] = useState([]);
  const [statusOptionsState, setStatusOptionsState] = useState([]);
  const [isRefreshingState, setIsRefreshingState] = useState(false);
  const [statusFilterState, setStatusFilterState] = useState('all');
  const [submitterFilterState, setSubmitterFilterState] = useState('all');
  const [assignedFilterState, setAssignedFilterState] = useState('all');
  const [sortFieldState, setSortFieldState] = useState('date');
  const [sortOrderState, setSortOrderState] = useState({ value: 'earliestToLatest', label: 'Nearest first' });

  const hasModuleAccess = Number(userAppData?.reproRequestsData?.perms ?? 0) === 1;
  const allowManageRequests = Boolean(userAppData?.reproRequestsData?.allowManageRequests);
  const canAccessRequests = hasModuleAccess && (requesterMode ? !allowManageRequests : allowManageRequests);

  function getStatusNameForFilter(request) {
    if (request?.status && typeof request.status === 'object') {
      return request.status.name || 'Open';
    }
    if (request?.status) {
      return request.status;
    }
    if (requestStateFilter === 'complete') {
      return 'Complete';
    }
    if (requestStateFilter === 'incomplete') {
      return 'Open';
    }
    if (request?.assignedUser && request.assignedUser !== 'None') {
      return 'Assigned';
    }
    return 'Open';
  }

  const statusOptionLookup = useMemo(() => {
    const lookup = new Map();
    if (Array.isArray(statusOptionsState) && statusOptionsState.length > 0) {
      statusOptionsState.forEach((status) => {
        if (status?.id && status?.name) {
          lookup.set(status.id, status.name);
        }
      });
    }
    dataState.forEach((request) => {
      if (request?.status && typeof request.status === 'object') {
        if (request.status.id && request.status.name) {
          lookup.set(request.status.id, request.status.name);
        }
      }
    });
    return lookup;
  }, [dataState, statusOptionsState]);

  const statusOptionsForFilter = useMemo(() => {
    const namesById = new Map();
    dataState.forEach((request) => {
      const submittingUser = request?.submittingUser || 'Unknown';
      if (submitterFilterState !== 'all' && submittingUser !== submitterFilterState) {
        return;
      }
      const assignedUser = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
      if (assignedFilterState !== 'all' && assignedUser !== assignedFilterState) {
        return;
      }
      const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
      const statusName = request?.status && typeof request.status === 'object'
        ? request.status.name
        : statusOptionLookup.get(statusId) || getStatusNameForFilter(request);
      if (statusId || statusName) {
        namesById.set(statusId || statusName, statusName);
      }
    });
    return Array.from(namesById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dataState, statusOptionLookup, submitterFilterState, assignedFilterState, requestStateFilter]);

  const submitterOptions = useMemo(() => {
    const names = new Set();
    dataState.forEach((request) => {
      const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
      const statusName = request?.status && typeof request.status === 'object'
        ? request.status.name
        : statusOptionLookup.get(statusId) || getStatusNameForFilter(request);
      if (statusFilterState !== 'all') {
        if (Array.isArray(statusOptionsState) && statusOptionsState.length > 0) {
          if (statusId) {
            if (statusId !== statusFilterState) {
              return;
            }
          } else {
            const matchedOption = statusOptionsState.find((status) => status?.name === statusName);
            if (!matchedOption || matchedOption.id !== statusFilterState) {
              return;
            }
          }
        } else if (statusName !== statusFilterState) {
          return;
        }
      }
      const assignedUser = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
      if (assignedFilterState !== 'all' && assignedUser !== assignedFilterState) {
        return;
      }
      names.add(request?.submittingUser || 'Unknown');
    });
    return Array.from(names).sort();
  }, [dataState, statusFilterState, assignedFilterState, statusOptionsState, statusOptionLookup, requestStateFilter]);

  const assignedUserOptions = useMemo(() => {
    const names = new Set();
    dataState.forEach((request) => {
      const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
      const statusName = request?.status && typeof request.status === 'object'
        ? request.status.name
        : statusOptionLookup.get(statusId) || getStatusNameForFilter(request);
      if (statusFilterState !== 'all') {
        if (Array.isArray(statusOptionsState) && statusOptionsState.length > 0) {
          if (statusId) {
            if (statusId !== statusFilterState) {
              return;
            }
          } else {
            const matchedOption = statusOptionsState.find((status) => status?.name === statusName);
            if (!matchedOption || matchedOption.id !== statusFilterState) {
              return;
            }
          }
        } else if (statusName !== statusFilterState) {
          return;
        }
      }
      const submittingUser = request?.submittingUser || 'Unknown';
      if (submitterFilterState !== 'all' && submittingUser !== submitterFilterState) {
        return;
      }
      const assignedName = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
      names.add(assignedName);
    });
    return Array.from(names).sort();
  }, [dataState, statusFilterState, submitterFilterState, statusOptionsState, statusOptionLookup, requestStateFilter]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(dataState)) {
      return [];
    }
    return dataState.filter((request) => {
      if (statusFilterState !== 'all') {
        const statusId = request?.status && typeof request.status === 'object' ? request.status.id : request?.status;
        const statusName = request?.status && typeof request.status === 'object'
          ? request.status.name
          : statusOptionLookup.get(statusId) || getStatusNameForFilter(request);
        if (Array.isArray(statusOptionsState) && statusOptionsState.length > 0) {
          if (statusId) {
            if (statusId !== statusFilterState) {
              return false;
            }
          } else {
            const matchedOption = statusOptionsState.find((status) => status?.name === statusName);
            if (!matchedOption || matchedOption.id !== statusFilterState) {
              return false;
            }
          }
        } else if (statusName !== statusFilterState) {
          return false;
        }
      }

      if (submitterFilterState !== 'all') {
        const submittingUser = request?.submittingUser || 'Unknown';
        if (submittingUser !== submitterFilterState) {
          return false;
        }
      }

      if (assignedFilterState !== 'all') {
        const assignedUser = request?.assignedUser && request.assignedUser !== 'None' ? request.assignedUser : 'Unassigned';
        if (assignedUser !== assignedFilterState) {
          return false;
        }
      }

      return true;
    });
  }, [dataState, statusFilterState, submitterFilterState, assignedFilterState, statusOptionsState, statusOptionLookup, requestStateFilter]);

  const sortOrderOptions = useMemo(() => {
    if (sortFieldState === 'id') {
      return [
        { value: 'lowToHigh', label: 'Low to high' },
        { value: 'highToLow', label: 'High to low' }
      ];
    }
    return [
      { value: 'earliestToLatest', label: 'Nearest first' },
      { value: 'latestToEarliest', label: 'Farthest first' }
    ];
  }, [sortFieldState]);

  const sortedFilteredData = useMemo(() => {
    if (!Array.isArray(filteredData)) {
      return [];
    }
    const sorted = [...filteredData];
    sorted.sort((leftRequest, rightRequest) => {
      if (sortFieldState === 'id') {
        const leftId = parseInt(leftRequest?.requestId, 10) || 0;
        const rightId = parseInt(rightRequest?.requestId, 10) || 0;
        return sortOrderState?.value === 'highToLow' ? rightId - leftId : leftId - rightId;
      }
      const leftDate = parseRequestDateValue(leftRequest?.requiredDate);
      const rightDate = parseRequestDateValue(rightRequest?.requiredDate);
      if (sortOrderState?.value === 'earliestToLatest') {
        return leftDate - rightDate;
      }
      return rightDate - leftDate;
    });
    return sorted;
  }, [filteredData, sortFieldState, sortOrderState]);

  const totalRequestsCount = sortedFilteredData.length;
  const currentRequestsCount = Array.isArray(pageDataState) ? pageDataState.length : 0;

  function paginate(array, pageSize, pageNumber) {
    return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  }

  async function apiCall(urlQuery) {
    setIsRefreshingState(true);
    try {
      const endpoint = requesterMode ? 'repro/user' : 'repro/admin';
      const response = await SEC_ApiCall(endpoint, { query: urlQuery }, setApiErrorState);
      const parsedResponse = typeof response === 'string' ? safeParse(response) : response;
      const responseData = Array.isArray(parsedResponse) ? parsedResponse : parsedResponse?.requests || [];
      setDataState(responseData);
      if (Array.isArray(parsedResponse?.reproAssignableUsers)) {
        setAssignableUsersState(parsedResponse.reproAssignableUsers);
      }
      if (Array.isArray(parsedResponse?.reproRequestStatuses)) {
        setStatusOptionsState(parsedResponse.reproRequestStatuses);
      }
      setCurrentPageState(1);
      setIsLoadedState(true);
    } catch (e) {
      setIsLoadedState(true);
      setApiErrorState('error');
      setApiErrorTextState(e);
    } finally {
      setIsRefreshingState(false);
    }
  }

  useEffect(() => {
    if (canAccessRequests) {
      apiCall('incomplete');
    } else {
      setIsLoadedState(true);
    }
  }, [canAccessRequests, requesterMode]);

  useEffect(() => {
    const resultsSize = sortedFilteredData.length;
    const perPage = resultsPerPageState === 'all' ? resultsSize : resultsPerPageState;
    const totalPages = perPage ? Math.ceil((resultsSize / perPage)) : 1;
    const safePage = Math.min(currentPageState, totalPages || 1);
    setNumberOfPagesState(totalPages || 1);
    if (resultsSize === 0) {
      setPageDataState([]);
      if (safePage !== 1) {
        setCurrentPageState(1);
      }
      return;
    }
    setPageDataState(paginate(sortedFilteredData, perPage, safePage));
    if (safePage !== currentPageState) {
      setCurrentPageState(safePage);
    }
  }, [sortedFilteredData, resultsPerPageState, currentPageState]);

  function dataReload() {
    apiCall(requestStateFilter);
  }

  if (!canAccessRequests || apiErrorState === 'error') {
    return <ApiError />;
  }

  function onResultsSelectorChange(event) {
    const querySelect = event.target.value;
    const perPageValue = querySelect === 'all' ? 'all' : parseInt(querySelect, 10);
    setResultsPerPageState(perPageValue);
    setCurrentPageState(1);
  }

  function nextPage() {
    if (currentPageState < numberOfPagesState) {
      setCurrentPageState(currentPageState + 1);
    }
  }

  function prevPage() {
    if (currentPageState > 1) {
      setCurrentPageState(currentPageState - 1);
    }
  }

  return (
    <div className='internal_main_div it-admin-requests-shell repro-requests-shell'>
      <Header
        title={requesterMode ? 'My Reprographics Requests' : 'Reprographics Requests'}
        subtitle={requesterMode ? 'Review and track the Reprographics requests you have submitted.' : 'Manage and review Reprographics requests.'}
        rightContent={<>
          <span className='module-page-header__badge'>{currentRequestsCount}/{totalRequestsCount}</span>
          <label className='module-page-header__field'>
            <span>Requests per page</span>
            <select defaultValue='12' onChange={onResultsSelectorChange}>
              <option value='12'>12</option>
              <option value='24'>24</option>
              <option value='48'>48</option>
              <option value='all'>All</option>
            </select>
          </label>
          <button
            className='module-page-header__button module-page-header__button--icon'
            type='button'
            onClick={dataReload}
            disabled={!isLoadedState}
            aria-label='Refresh Reprographics requests'
          >
            {isRefreshingState ? <CgSpinner className='reload-icon it-admin-spinner' /> : <LuRefreshCw className='reload-icon' />}
          </button>
        </>}
      />

      <div className="it-admin-content-grid">
        <div className="it-admin-content-main">
          <ReproRequestsCards
            data={pageDataState}
            isLoaded={isLoadedState}
            apiError={apiErrorState}
            selectorValue={requestStateFilter}
            onRefresh={() => apiCall(requestStateFilter)}
            assignableUsers={assignableUsersState}
            statusOptions={statusOptionsState}
            requesterMode={requesterMode}
          />
          {isLoadedState && (
            <div className="nextRequestPageDiv">
              <div className="pageNumberButtons" onClick={prevPage}><FaArrowLeft color="#fb9233" /></div>
              <div className="pageNumberDiv">{currentPageState} / {numberOfPagesState}</div>
              <div className="pageNumberButtons" onClick={nextPage}><FaArrowRight color="#fb9233" /></div>
            </div>
          )}
        </div>

        <aside className="it-admin-filter-sidebar">
          <div className="it-admin-filter-panel">
            <label className="it-admin-filter-field">
              <span className="it-admin-filter-label">Request state</span>
              <select
                className="it-admin-filter-select"
                value={requestStateFilter}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setRequestStateFilter(newValue);
                  setCurrentPageState(1);
                  apiCall(newValue);
                }}
              >
                <option value="incomplete">Incomplete</option>
                <option value="complete">Complete</option>
              </select>
            </label>
            <label className="it-admin-filter-field">
              <span className="it-admin-filter-label">Request status</span>
              <select
                className="it-admin-filter-select"
                value={statusFilterState}
                onChange={(event) => {
                  setStatusFilterState(event.target.value);
                  setCurrentPageState(1);
                }}
              >
                <option value="all">All statuses</option>
                {statusOptionsForFilter.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </label>
            {!requesterMode && (
              <label className="it-admin-filter-field">
                <span className="it-admin-filter-label">Submitting user</span>
                <select
                  className="it-admin-filter-select"
                  value={submitterFilterState}
                  onChange={(event) => {
                    setSubmitterFilterState(event.target.value);
                    setCurrentPageState(1);
                  }}
                >
                  <option value="all">All submitters</option>
                  {submitterOptions.map((user) => (
                    <option key={user} value={user}>
                      {user}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="it-admin-filter-field">
              <span className="it-admin-filter-label">Assigned user</span>
              <select
                className="it-admin-filter-select"
                value={assignedFilterState}
                onChange={(event) => {
                  setAssignedFilterState(event.target.value);
                  setCurrentPageState(1);
                }}
              >
                <option value="all">All assigned</option>
                {assignedUserOptions.map((user) => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="it-admin-filter-clear"
              onClick={() => {
                setRequestStateFilter('incomplete');
                setStatusFilterState('all');
                setSubmitterFilterState('all');
                setAssignedFilterState('all');
                setCurrentPageState(1);
                apiCall('incomplete');
              }}
              disabled={
                requestStateFilter === 'incomplete'
                && statusFilterState === 'all'
                && submitterFilterState === 'all'
                && assignedFilterState === 'all'
              }
            >
              Clear filters
            </button>
          </div>
          <div className="it-admin-filter-panel it-admin-sort-panel">
            <p className="it-admin-sort-title">Sort requests</p>
            <div className="it-admin-sort-radio-group">
              <label className="it-admin-sort-radio-option">
                <input
                  type="radio"
                  name="reproAdminSortField"
                  value="date"
                  checked={sortFieldState === 'date'}
                  onChange={() => {
                    setSortFieldState('date');
                    setSortOrderState({ value: 'earliestToLatest', label: 'Nearest first' });
                    setCurrentPageState(1);
                  }}
                />
                Required Date
              </label>
              <label className="it-admin-sort-radio-option">
                <input
                  type="radio"
                  name="reproAdminSortField"
                  value="id"
                  checked={sortFieldState === 'id'}
                  onChange={() => {
                    setSortFieldState('id');
                    setSortOrderState({ value: 'lowToHigh', label: 'Low to high' });
                    setCurrentPageState(1);
                  }}
                />
                ID number
              </label>
            </div>
            <label className="it-admin-filter-field">
              <span className="it-admin-filter-label">Sort order</span>
              <Select
                className="it-admin-sort-order-select"
                classNamePrefix="it-admin-sort"
                options={sortOrderOptions}
                value={sortOrderState}
                onChange={(option) => {
                  if (!option) {
                    return;
                  }
                  setSortOrderState(option);
                  setCurrentPageState(1);
                }}
                isSearchable={false}
              />
            </label>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ReproRequestsCards({ data, isLoaded, apiError, selectorValue, onRefresh, assignableUsers, statusOptions, requesterMode }) {
  const navigate = useNavigate();

  if (!isLoaded) {
    return (
      <div className="it-admin-cards-empty">
        <LoadingComp />
      </div>
    );
  }

  if (apiError) {
    return <ApiError />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="it-admin-cards-empty">
        <p className="it-admin-cards-loading">No requests to show</p>
      </div>
    );
  }

  return (
    <div className="it-admin-cards-grid">
      {data.map((request) => (
        <ReproRequestCard
          key={request.requestId}
          request={request}
          selectorValue={selectorValue}
          onRefresh={onRefresh}
          assignableUsers={assignableUsers}
          statusOptions={statusOptions}
          requesterMode={requesterMode}
          onOpenFull={() => navigate(requesterMode
            ? `/app/repro/user/requests/srv?id=${request.requestId}`
            : `/app/repro/admin/requests/srv?id=${request.requestId}`)}
        />
      ))}
    </div>
  );
}

function ReproRequestCard({ request, selectorValue, onRefresh, assignableUsers, statusOptions, onOpenFull, requesterMode }) {
  const [activePanel, setActivePanel] = useState(null);

  const statusValue = useMemo(() => {
    if (request?.status && typeof request.status === 'object') {
      return request.status.name || 'Open';
    }
    if (request?.status) {
      return request.status;
    }
    if (selectorValue === 'complete') {
      return 'Complete';
    }
    if (request?.assignedUser && request.assignedUser !== 'None') {
      return 'Assigned';
    }
    return 'Open';
  }, [request, selectorValue]);

  const isComplete = selectorValue === 'complete' || request?.completed === 1 || statusValue === 'Complete';

  const statusColour = useMemo(() => {
    if (request?.status && typeof request.status === 'object' && request.status.colour) {
      return request.status.colour;
    }
    return '#f9b233';
  }, [request]);

  const statusTextColour = useMemo(() => {
    if (request?.status && typeof request.status === 'object' && request.status.textColour) {
      return request.status.textColour;
    }
    return '#ffffff';
  }, [request]);

  const statusSelectOptions = useMemo(() => {
    if (!Array.isArray(statusOptions)) {
      return [];
    }
    const currentStatusId =
      request?.status && typeof request.status === 'object'
        ? request.status.id
        : request?.status;
    return statusOptions
      .filter((status) => status?.id && status.id !== currentStatusId)
      .map((status) => ({
        value: status.id,
        label: status.name
      }));
  }, [statusOptions, request]);

  const handlePanelToggle = (panel) => {
    if (activePanel === panel) {
      setActivePanel(null);
      return;
    }
    setActivePanel(panel);
  };

  const handleRefresh = () => {
    onRefresh();
    setActivePanel(null);
  };

  const showAssign = !requesterMode && !isComplete;

  return (
    <div className={`it-admin-card${activePanel ? ' it-admin-card-active' : ''}`}>
      <div className="it-admin-card-main">
        <div className="it-admin-card-header">
          <div>
            <p className="it-admin-card-kicker">Repro Request #{request.requestId}</p>
            <h3 className="it-admin-card-title">{request.requestTitle}</h3>
          </div>
          <span className="it-admin-card-status" style={{ background: statusColour, color: statusTextColour }}>
            {statusValue}
          </span>
        </div>

        <div className="it-admin-card-meta">
          <p><strong>Submitted by:</strong> {request.submittingUser || 'Unknown'}</p>
          {request.assignedUser && request.assignedUser !== 'None' && (
            <p><strong>Assigned to:</strong> {request.assignedUser}</p>
          )}
          {request.requiredDate && (
            <p><strong>Required date:</strong> {formatDate(request.requiredDate)}</p>
          )}
          <p><strong>Copies:</strong> {request.copyCount || 0}</p>
        </div>

        {activePanel === 'details' && (
          <div className="it-admin-card-details repro-request-card-details">
            <p><strong>Submitted on:</strong> {formatDateTime(request.submissionTimestamp)}</p>
            <p><strong>Paper:</strong> {formatPaperSummary(request)}</p>
            <p><strong>Finishing:</strong> {formatFinishingSummary(request)}</p>
            <RequestFilesList
              files={request.files}
              folder="repro"
              itemId={request.requestId}
              title="Files"
              emptyText="Files: None attached"
              className="repro-request-card-files"
            />
            {Array.isArray(request.links) && request.links.length > 0 && (
              <div className="repro-request-card-links">
                <p><strong>Links:</strong></p>
                <p className="repro-request-card-link-warning">
                  School Engine does not check or verify link contents. Only open links you trust.
                </p>
                {request.links.map((link, index) => (
                  <ExpandableLinkButton link={link} key={`${link.url}-${index}`} />
                ))}
              </div>
            )}
            <p className="it-admin-card-description">
              <strong>Instructions:</strong> {request.otherInstructions || 'No additional instructions'}
            </p>
          </div>
        )}
      </div>

      <div className="it-admin-card-actions">
        <div className="it-admin-card-actions-icons">
          {showAssign && (
            <AssignUser
              onClick={() => handlePanelToggle('assign')}
              isActive={activePanel === 'assign'}
            />
          )}
          <AddNote
            onClick={() => handlePanelToggle('note')}
            isActive={activePanel === 'note'}
          />
          {!requesterMode && (
            <UpdateStatusButton
              onClick={() => handlePanelToggle('status')}
              isActive={activePanel === 'status'}
            />
          )}
          {isComplete ? (
            <MarkIncompleteButton
              onClick={() => handlePanelToggle('incomplete')}
              isActive={activePanel === 'incomplete'}
            />
          ) : (
            <MarkCompleteButton
              onClick={() => handlePanelToggle('complete')}
              isActive={activePanel === 'complete'}
            />
          )}
        </div>
        <div className="it-admin-card-actions-icons it-admin-card-actions-icons-right">
          <button
            type="button"
            className="it-admin-card-icon-button"
            onClick={() => handlePanelToggle('details')}
            aria-label={activePanel === 'details' ? 'Hide details' : 'View details'}
            title={activePanel === 'details' ? 'Hide details' : 'View details'}
          >
            <FiInfo />
          </button>
          <button
            type="button"
            className="it-admin-card-icon-button it-admin-card-icon-button-secondary"
            onClick={onOpenFull}
            aria-label="Open full view"
            title="Open full view"
          >
            <FiExternalLink />
          </button>
        </div>
      </div>

      {!requesterMode && activePanel === 'assign' && (
        <div className="it-admin-card-panel">
          {assignableUsers.length > 0 ? (
            <AssignUserWithNote
              usersArray={assignableUsers}
              requestId={request.requestId}
              url="repro/SingleRequest/assign"
              updateApiCallFunction={() => handleRefresh()}
              setActiveOption={() => setActivePanel(null)}
              cardView
              showPrivateToggle
              uploadModuleType="repro"
              permissionsKey="reproRequestsData"
            />
          ) : (
            <p className="it-admin-card-loading">No assignable Reprographics users found.</p>
          )}
        </div>
      )}

      {activePanel === 'note' && (
        <div className="it-admin-card-panel">
          <NoteInputComponent
            requestId={request.requestId}
            url="repro/SingleRequest/notes"
            updateApiCallFunction={() => handleRefresh()}
            placeholder="Enter your note here..."
            cardView
            showPrivateToggle={!requesterMode}
            uploadModuleType="repro"
            permissionsKey="reproRequestsData"
          />
        </div>
      )}

      {!requesterMode && activePanel === 'status' && (
        <div className="it-admin-card-panel">
          <UpdateStatusSelect
            requestId={request.requestId}
            url="repro/SingleRequest/status"
            updateApiCallFunction={() => handleRefresh()}
            setActiveOption={() => setActivePanel(null)}
            cardView
            options={statusSelectOptions}
          />
        </div>
      )}

      {activePanel === 'complete' && (
        <div className="it-admin-card-panel">
          <NoteInputComponent
            requestId={request.requestId}
            url="repro/SingleRequest/complete"
            updateApiCallFunction={() => handleRefresh()}
            placeholder="Enter your completion note..."
            cardView
            showPrivateToggle={!requesterMode}
            uploadModuleType="repro"
            permissionsKey="reproRequestsData"
          />
        </div>
      )}

      {activePanel === 'incomplete' && (
        <div className="it-admin-card-panel">
          <NoteInputComponent
            requestId={request.requestId}
            url="repro/SingleRequest/incomplete"
            updateApiCallFunction={() => handleRefresh()}
            placeholder="Enter your reason for reopening..."
            cardView
            showPrivateToggle={!requesterMode}
            uploadModuleType="repro"
            permissionsKey="reproRequestsData"
          />
        </div>
      )}
    </div>
  );
}

function formatDate(rawValue) {
  if (!rawValue) {
    return 'Not set';
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(parsed);
}

function formatDateTime(rawValue) {
  if (!rawValue) {
    return 'Not set';
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function formatPaperSummary(request) {
  const values = [
    request.paperSize ? `Size: ${request.paperSize}` : '',
    request.paperColour ? `Paper colour: ${formatReproOptionValue(request.paperColour)}` : '',
    request.printColour ? `Print: ${formatReproOptionValue(request.printColour)}` : '',
    request.material ? `Material: ${formatReproOptionValue(request.material)}` : '',
    request.sides ? `Sides: ${formatReproOptionValue(request.sides)}` : '',
  ].filter(Boolean);
  return values.length > 0 ? values.join(', ') : 'Not set';
}

function formatFinishingSummary(request) {
  const values = [
    request.staples && request.staples !== 'none' ? `Staples: ${formatReproOptionValue(request.staples)}` : '',
    request.binding && request.binding !== 'no' ? `Binding: ${formatReproOptionValue(request.binding)}` : '',
  ].filter(Boolean);
  return values.length > 0 ? values.join(' / ') : 'None';
}

function formatReproOptionValue(value) {
  const displayValues = {
    blackWhite: 'Black and white',
    colour: 'Colour',
    singleSided: 'Single sided',
    doubleSided: 'Double sided',
    cornerSingle: 'Corner single',
    edgesDouble: 'Edges double',
    none: 'None',
    yes: 'Yes',
    no: 'No',
  };
  if (displayValues[value]) {
    return displayValues[value];
  }
  if (!value) {
    return 'Not set';
  }
  return String(value).replace(/([a-z])([A-Z])/g, '$1 $2');
}

function safeParse(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

function parseRequestDateValue(rawValue) {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return 0;
  }
  if (typeof rawValue === 'number') {
    return rawValue;
  }
  const directParse = new Date(rawValue).getTime();
  if (!Number.isNaN(directParse)) {
    return directParse;
  }
  if (typeof rawValue !== 'string') {
    return 0;
  }
  const normalisedValue = rawValue.replace(',', '').trim();
  const ukDateMatch = normalisedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (!ukDateMatch) {
    return 0;
  }
  const day = parseInt(ukDateMatch[1], 10);
  const month = parseInt(ukDateMatch[2], 10) - 1;
  const year = parseInt(ukDateMatch[3], 10);
  const hour = parseInt(ukDateMatch[4] || '0', 10);
  const minute = parseInt(ukDateMatch[5] || '0', 10);
  return new Date(year, month, day, hour, minute).getTime();
}
